import type { EnvironmentId, ScopedThreadRef } from "@cmmd-forge/contracts";
import { useCallback, type ReactNode } from "react";

import type { useThreadActions } from "~/hooks/useThreadActions";
import { SidebarThreadRow, type SidebarThreadRowProps } from "./SidebarThreadRow";
import { ActivityThreadCard } from "./ActivityThreadCard";
import { ActivityArchivedRow } from "./ActivityArchivedRow";
import { resolveThreadOrigin } from "./threadOrigin";
import type { ProjectDisplayEntry } from "./projectDisplayIndex";
import type { SavedEnvironmentRuntimeIndex } from "./sidebarThreadRuntimeIdentity";
export type ThreadRowVariant = "project" | "card" | "archived";

export interface RenderThreadRowDeps {
  readonly resolveProjectDisplay: (anchor: {
    readonly environmentId: string;
    readonly projectId: string;
  }) => ProjectDisplayEntry;
  readonly resolveEnvironmentLabel: (environmentId: EnvironmentId) => string;
  readonly primaryEnvironmentId: EnvironmentId | null;
  /** Lets the origin badge recognise a runtime connected under its durable key. */
  readonly savedEnvironmentRegistry: SavedEnvironmentRuntimeIndex;
  readonly unarchiveThread: ReturnType<typeof useThreadActions>["unarchiveThread"];
  readonly unsettleThread: ReturnType<typeof useThreadActions>["unsettleThread"];
}

/**
 * Three arrangements, different components on purpose: a project row already
 * knows its project so it spends width on the title; a flat-list card
 * carries its own project/branch/PR; an archived row is scanned, not acted
 * on, so it drops everything but identity and age. Behaviour is shared via
 * `useSidebarThreadRow`.
 */
export function renderActivityThreadRow(
  rowProps: SidebarThreadRowProps,
  variant: ThreadRowVariant,
  deps: RenderThreadRowDeps,
) {
  if (variant === "project") return <SidebarThreadRow {...rowProps} />;
  const display = deps.resolveProjectDisplay(rowProps.thread);
  const activityProps = {
    ...rowProps,
    projectName: display.name,
    ...(display.canonicalIdentity ? { projectIcon: display.canonicalIdentity } : {}),
    // The card's details panel names the machine. The compact row does
    // not — it shows the environment as a badge instead — so this is
    // resolved here rather than being threaded through every caller.
    environmentLabel: deps.resolveEnvironmentLabel(rowProps.thread.environmentId),
    // Upstream's `isRemote`, derived the same way — Activity never passed
    // the older `environmentBadge` (the render prop's 4th param, called
    // with two), so remoteness had no way onto a card until this. One
    // canonical rule for every location marker: no Forge Cloud sprite
    // wearing the user's own Windows glyph.
    origin: resolveThreadOrigin({
      threadEnvironmentId: rowProps.thread.environmentId,
      currentEnvironmentId: deps.primaryEnvironmentId,
      savedEnvironments: deps.savedEnvironmentRegistry,
    }),
  };
  if (variant === "archived") {
    // Archived vs merely-settled need different restore mutations; see
    // ActivityArchivedRow's `onRestore` doc.
    const hasArchiveWorkflow = rowProps.thread.archive?.state != null;
    const onRestore =
      rowProps.thread.archivedAt != null || hasArchiveWorkflow
        ? (threadRef: ScopedThreadRef) =>
            deps.unarchiveThread(threadRef, { threadSnapshot: rowProps.thread })
        : (threadRef: ScopedThreadRef) =>
            deps.unsettleThread(threadRef, { threadSnapshot: rowProps.thread });
    return <ActivityArchivedRow {...activityProps} onRestore={onRestore} />;
  }
  return <ActivityThreadCard {...activityProps} />;
}

/** The Activity list's row renderer, with its dependencies assembled. */
export function useActivityThreadRowRenderer(
  deps: RenderThreadRowDeps,
): (rowProps: SidebarThreadRowProps, variant: ThreadRowVariant) => ReactNode {
  const { resolveProjectDisplay, resolveEnvironmentLabel, primaryEnvironmentId } = deps;
  const { savedEnvironmentRegistry, unarchiveThread, unsettleThread } = deps;

  return useCallback(
    (rowProps: SidebarThreadRowProps, variant: ThreadRowVariant) =>
      renderActivityThreadRow(rowProps, variant, {
        resolveProjectDisplay,
        resolveEnvironmentLabel,
        primaryEnvironmentId,
        savedEnvironmentRegistry,
        unarchiveThread,
        unsettleThread,
      }),
    [
      primaryEnvironmentId,
      resolveEnvironmentLabel,
      resolveProjectDisplay,
      savedEnvironmentRegistry,
      unarchiveThread,
      unsettleThread,
    ],
  );
}
